'use client'

import Image from 'next/image'
import Link from 'next/link'
import { RateCtaLabel, StarRating } from '@/components/star-rating'

interface ResultEntity {
  id: string
  slug: string
  name: string
  description: string | null
  imageUrl: string | null
  type: string
  currentRating: number
  totalRatings: number
  parentName: string | null
  grandparentName: string | null
}

interface BestOfResultsProps {
  results: ResultEntity[]
}

export function BestOfResults({ results }: BestOfResultsProps) {
  if (results.length === 0) {
    return (
      <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
        <p className="text-slate-600 text-lg">No rated items found for this selection.</p>
        <p className="text-slate-500 mt-2">Try broadening your search or be the first to rate!</p>
      </div>
    )
  }

  const getLocationString = (entity: ResultEntity) => {
    const parts: string[] = []
    if (entity.parentName) parts.push(entity.parentName)
    if (entity.grandparentName) parts.push(entity.grandparentName)
    return parts.join(' • ')
  }

  return (
    <div className="space-y-4">
      {results.map((entity, index) => (
        <Link
          key={entity.id}
          href={`/rate/${entity.slug}`}
          className="block bg-white border border-slate-200 rounded-lg shadow-sm hover:border-[#1E4289] hover:shadow-md transition-all overflow-hidden group"
        >
          <div className="flex flex-col sm:flex-row">
            {/* Rank Badge */}
            <div className="flex-shrink-0 flex items-center justify-center w-full sm:w-16 h-12 sm:h-auto bg-gradient-to-br from-[#1E4289] to-[#0f2654]">
              <span className="text-2xl font-bold text-[#FEDB1A]">#{index + 1}</span>
            </div>

            {/* Image */}
            <div className="relative w-full sm:w-32 h-32 flex-shrink-0 bg-slate-100">
              <Image
                src={entity.imageUrl || '/no_image.png'}
                alt={entity.name}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                unoptimized
              />
            </div>

            {/* Content */}
            <div className="flex-1 p-4">
              <h3 className="text-lg font-bold text-slate-800 group-hover:text-[#1E4289] transition-colors">
                {entity.name}
              </h3>

              {/* Location */}
              {getLocationString(entity) && (
                <p className="text-xs text-slate-500 mt-1">
                  {getLocationString(entity)}
                </p>
              )}

              {/* Rating */}
              <div className="mt-2">
                <div className="flex items-center gap-3 mb-3">
                  <StarRating rating={entity.currentRating} size="sm" showNumber />
                  <span className="text-xs text-slate-500">
                    {entity.totalRatings} {entity.totalRatings === 1 ? 'rating' : 'ratings'}
                  </span>
                </div>
                <RateCtaLabel />
              </div>

              {/* Description */}
              {entity.description && (
                <p className="text-slate-600 text-sm mt-2">
                  {entity.description}
                </p>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
